import { Settings, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SafeAvatar } from '../../components/ui/SafeAvatar';

interface User {
  id: string | number;
  name: string;
  avatar?: string;
  role: string;
}

interface AvatarMenuProps {
  user: User;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onProfile: () => void;
  onSettings: () => void;
  onLogout: () => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

/**
 * Avatar dropdown menu component
 */
export const AvatarMenu: React.FC<AvatarMenuProps> = ({
  user,
  isOpen,
  onToggle,
  onClose,
  onProfile,
  onSettings,
  onLogout,
  menuRef,
}) => {
  const { t } = useTranslation();
  const handleMenuAction = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <div className="flex items-center relative" ref={menuRef}>
      <button
        className="focus:outline-none transition-all active:scale-95"
        onClick={onToggle}
        title={t('navigation.userMenu.title')}
        aria-haspopup="menu"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls="user-menu"
        aria-label={t('navigation.userMenu.open')}
      >
        <SafeAvatar 
          src={user.avatar} 
          name={user.name} 
          size="sm" 
          className="hover:shadow-xl transition-shadow"
        />
      </button>

      {/* Dropdown menu */}
      <div
        id="user-menu"
        className={`absolute right-0 z-50 w-64 max-w-xs bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl animate-fade-in-down transition-all duration-200 origin-top-right ${
          isOpen
            ? 'scale-100 opacity-100 pointer-events-auto translate-y-2'
            : 'scale-95 opacity-0 pointer-events-none -translate-y-2'
        }`}
        style={{ top: '100%' }}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="user-menu-button"
        aria-hidden={isOpen ? 'false' : 'true'}
      >
        {/* Profile option */}
        <button
          className="w-full text-left px-4 py-2 hover:bg-primary-50 focus:bg-primary-100 text-slate-700 transition-colors duration-150 focus:outline-none"
          role="menuitem"
          onClick={() => handleMenuAction(onProfile)}
        >
          {t('navigation.userMenu.profile')}
        </button>

        {/* Settings option */}
        <button
          className="w-full text-left px-4 py-2 hover:bg-primary-50 focus:bg-primary-100 text-slate-700 transition-colors duration-150 flex items-center gap-2 focus:outline-none"
          role="menuitem"
          onClick={() => handleMenuAction(onSettings)}
        >
          <Settings size={18} className="inline" aria-hidden="true" />
          {t('navigation.userMenu.settings')}
        </button>

        {/* Logout option */}
        <button
          className="w-full text-left px-4 py-3 hover:bg-red-50 focus:bg-red-100 text-red-600 transition-colors duration-150 focus:outline-none font-bold text-xs uppercase tracking-widest"
          role="menuitem"
          onClick={() => handleMenuAction(onLogout)}
        >
          {t('navigation.userMenu.logout')}
        </button>
      </div>

      {/* User name */}
      <span className="ml-2 text-sm font-medium text-slate-700 hidden sm:block">
        {user.name}
      </span>
    </div>
  );
};
